
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';

import { ProfileFormValues } from './ProfileFormSchema';
import { BasicInfoFields } from './form-fields/BasicInfoFields';
import { BeltRankFields } from './form-fields/BeltRankFields';
import { HomeGymField } from './form-fields/HomeGymField';
import { TrainingPreferenceField } from './form-fields/TrainingPreferenceField';
import { DateFields } from './form-fields/DateFields';
import { EmailPreferencesField } from './form-fields/EmailPreferencesField';
import { SubmitButton } from './form-fields/SubmitButton';
import { CameraAvatarUpload } from './form-fields/CameraAvatarUpload';
import { useAvatarManagement } from './hooks/useAvatarManagement';
import { usePermissions } from '@/contexts/unified-permissions';

interface ProfileFormProps {
  form: UseFormReturn<ProfileFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: ProfileFormValues, avatarUrl?: string | null) => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  form,
  isSubmitting,
  onSubmit,
}) => {
  const { signOut, user } = usePermissions();
  const [showCustomBelt, setShowCustomBelt] = useState(false);

  // Integrate avatar management
  const {
    avatarUrl,
    setAvatarUrl,
    isUploading,
    onAvatarChange
  } = useAvatarManagement(user?.id);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleFormSubmit = (data: ProfileFormValues) => {
    // Pass avatar URL to the submission handler
    onSubmit(data, avatarUrl);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
            {/* Avatar Section */}
            <CameraAvatarUpload
              userId={user?.id || ''}
              avatarUrl={avatarUrl}
              setAvatarUrl={setAvatarUrl}
              isUploading={isUploading}
              onAvatarChange={onAvatarChange}
            />

            {/* Basic Information */}
            <BasicInfoFields control={form.control} />
            
            {/* Belt Information */}
            <BeltRankFields 
              control={form.control} 
              showCustomBelt={showCustomBelt}
              setShowCustomBelt={setShowCustomBelt}
            />
            
            {/* Home Gym */}
            <HomeGymField control={form.control} />
            
            {/* Training Preference */}
            <TrainingPreferenceField control={form.control} />
            
            {/* Date Fields */}
            <DateFields control={form.control} />
            
            {/* Email Preferences */}
            <EmailPreferencesField control={form.control} />
            
            {/* Submit Button */}
            <SubmitButton 
              isLoading={isSubmitting} 
              onSignOut={handleSignOut}
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
